import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { roleplay } from 'services/api';
import { RoleplayStreamError } from 'services/api/roleplayStream';
import { startRoleplayPerformance } from './roleplayPerformance';
import type {
  RoleplayExpression,
  RoleplayMode,
  RoleplayOpeningPreparation,
  RoleplayRuntimeAction,
  RoleplayRuntimeActionOutcome as RuntimeActionOutcome,
  RoleplayRuntimeReady,
  RoleplaySession as RoleplaySessionRecord,
  RoleplayScenario,
  RoleplayTurnProgress,
} from 'services/api/roleplayClient';

export type RoleplayScreen = 'prebrief' | 'practice' | 'support' | 'debrief';

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof RoleplayStreamError) return error.message;
  const data = (error as { response?: { data?: { detail?: string; message?: string } } })?.response?.data;
  return data?.detail ?? data?.message ?? fallback;
};

/** True when the server answered (any HTTP status), as opposed to a transport failure. */
const hasHttpStatus = (error: unknown) =>
  typeof (error as { response?: { status?: unknown } })?.response?.status === 'number';

const makeEventId = (prefix: string) => {
  const randomId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${randomId}`;
};

const isTerminalStatus = (status: RoleplaySessionRecord['status']) =>
  status === 'completed' || status === 'ended_early';

const screenForSession = (session: RoleplaySessionRecord | null): RoleplayScreen => {
  if (!session) return 'prebrief';
  if (session.status === 'crisis_paused') return 'support';
  if (isTerminalStatus(session.status)) return 'debrief';
  return 'practice';
};

/** A persona reply or closing the learner has not seen in this attempt yet. */
const hasNewPersonaTurn = (
  previous: RoleplaySessionRecord,
  next: RoleplaySessionRecord,
) => {
  const previousTurnIds = new Set(previous.turns.map((turn) => turn.id));
  return next.turns.some(
    (turn) => turn.speaker === 'persona'
      && !turn.metadata?.safety_only
      && !previousTurnIds.has(turn.id),
  );
};

/**
 * The attempt ends on a persona reply the learner should still be able to
 * read. A Text `preview` frame can already carry that final reply before the
 * terminal frame arrives, so "new since the previous payload" is not enough.
 */
const endsWithPersonaReply = (next: RoleplaySessionRecord) => {
  const conversational = next.turns.filter((turn) => !turn.metadata?.safety_only);
  const last = conversational[conversational.length - 1];
  return Boolean(last && last.speaker === 'persona');
};

/**
 * The latest adopted server learner clock, projected locally. `remainingMs`
 * was true at `observedAt`; it only counts down while the server reported the
 * clock running, so a paused countdown is never restarted by a wall-time
 * snapshot.
 */
type LearnerClockProjection = {
  sessionId: number;
  epoch: string;
  serverNow: number;
  remainingMs: number;
  observedAt: number;
  running: boolean;
};

const projectSecondsLeft = (clock: LearnerClockProjection, now = Date.now()) => {
  const locallyElapsed = clock.running ? Math.max(0, now - clock.observedAt) : 0;
  return Math.max(0, Math.ceil((clock.remainingMs - locallyElapsed) / 1000));
};

export const useRoleplaySession = (
  flowName: string,
  flowRunId: number,
  questionId: number | null = null,
  enabled = true,
) => {
  const [scenario, setScenario] = useState<RoleplayScenario | null>(null);
  const [session, setSession] = useState<RoleplaySessionRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [openingPreparation, setOpeningPreparation] = useState<RoleplayOpeningPreparation | null>(null);
  const [openingPreparing, setOpeningPreparing] = useState(false);
  const [terminalTransitionPending, setTerminalTransitionPending] = useState(false);
  const [runtimeWaiting, setRuntimeWaiting] = useState(false);
  // True from the moment a learner turn request is sent until it settles, even
  // before any stream frame arrives, so safety polling can run alongside it.
  const [turnActive, setTurnActive] = useState(false);
  const [learnerClock, setLearnerClock] = useState<LearnerClockProjection | null>(null);
  const timerEndRequested = useRef(false);
  const endingRef = useRef(false);
  const sessionIdRef = useRef<number | null>(null);
  const sessionRef = useRef<RoleplaySessionRecord | null>(null);
  const turnRequestRef = useRef<AbortController | null>(null);
  const requestEpochRef = useRef(0);
  // Bumped by End, a new attempt, the prebrief, safety, unmount and explicit
  // recovery. Results captured under an older epoch are never applied.
  const operationEpochRef = useRef(0);
  const refreshRequestRef = useRef<AbortController | null>(null);
  const retryTurnRef = useRef<{ text: string; eventId: string } | null>(null);
  const learnerClockRef = useRef<LearnerClockProjection | null>(null);
  const immediateTextTerminalRef = useRef(false);
  const runtimeRef = useRef<{ sessionId: number; runtimeId: string; sequence: number } | null>(null);
  const runtimeQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  // Monotonic count of committed session snapshots (see commitSession).
  const sessionRevisionRef = useRef(0);
  // Bumped by explicit recoveries that restore usable learner input on the same
  // attempt (actor/evaluator retry, safety resolution). A `suspend` enqueued by
  // the disconnect that preceded such a recovery is obsolete once it lands.
  const inputRecoveryEpochRef = useRef(0);
  const textPerformanceRef = useRef<{
    recorder: ReturnType<typeof startRoleplayPerformance>;
    sessionId: number;
    startedAt: number;
    learnerTurnId: number | null;
    firstVisible: boolean;
    requestCompleted: boolean;
  } | null>(null);
  const cancelRefresh = useCallback(() => {
    refreshRequestRef.current?.abort();
    refreshRequestRef.current = null;
  }, []);
  const cancelTurn = useCallback(() => {
    textPerformanceRef.current?.recorder.finish('cancelled', 'interrupted');
    textPerformanceRef.current = null;
    requestEpochRef.current += 1;
    operationEpochRef.current += 1;
    turnRequestRef.current?.abort();
    turnRequestRef.current = null;
    setTurnActive(false);
    cancelRefresh();
  }, [cancelRefresh]);
  const terminalTransitionDeferredRef = useRef(false);
  const terminalTransitionModeRef = useRef<RoleplayMode | null>(null);
  const deferredTerminalSessionRef = useRef<RoleplaySessionRecord | null>(null);
  const openingPreparationKey = `${flowName}:${flowRunId}:${questionId ?? ''}`;
  const openingPreparationKeyRef = useRef(openingPreparationKey);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const openingPreparationRef = useRef<{
    key: string;
    value: RoleplayOpeningPreparation;
  } | null>(null);
  const openingPreparationPromiseRef = useRef<{
    key: string;
    value: Promise<RoleplayOpeningPreparation | null>;
  } | null>(null);
  openingPreparationKeyRef.current = openingPreparationKey;
  const isCurrent = useCallback(() => enabledRef.current
    && openingPreparationKeyRef.current === openingPreparationKey, [openingPreparationKey]);
  const isOperationCurrent = useCallback(
    (sessionId: number, epoch: number) => isCurrent()
      && sessionIdRef.current === sessionId && operationEpochRef.current === epoch,
    [isCurrent],
  );

  const adoptLearnerClock = useCallback((next: RoleplaySessionRecord) => {
    const snapshot = next.learner_clock;
    const current = learnerClockRef.current;
    if (snapshot === undefined) {
      // A payload without the field never restarts a projected clock; only an
      // attempt that never negotiated one follows the legacy wall-time value.
      if (current?.sessionId === next.id) return;
      learnerClockRef.current = null;
      setLearnerClock(null);
      setSecondsLeft(next.seconds_left);
      return;
    }
    if (snapshot === null) {
      learnerClockRef.current = null;
      setLearnerClock(null);
      setSecondsLeft(next.seconds_left);
      return;
    }
    const serverNow = Date.parse(snapshot.server_now);
    if (
      current?.sessionId === next.id
      && Number.isFinite(serverNow)
      && serverNow < current.serverNow
    ) return; // An older snapshot cannot move the projection backwards.
    const remainingMs = typeof snapshot.remaining_ms === 'number'
      ? snapshot.remaining_ms
      : Math.max(0, next.scenario.max_active_seconds * 1000 - snapshot.observed_elapsed_ms);
    const projection: LearnerClockProjection = {
      sessionId: next.id,
      epoch: snapshot.epoch,
      serverNow: Number.isFinite(serverNow) ? serverNow : current?.serverNow ?? 0,
      remainingMs,
      observedAt: Date.now(),
      running: snapshot.running_since !== null && !snapshot.terminal,
    };
    learnerClockRef.current = projection;
    setLearnerClock(projection);
    setSecondsLeft(projectSecondsLeft(projection));
  }, []);

  const commitSession = useCallback((next: RoleplaySessionRecord) => {
    if (!enabledRef.current || openingPreparationKeyRef.current !== openingPreparationKey) return;
    const isNewSession = sessionIdRef.current !== next.id;
    if (isNewSession) {
      timerEndRequested.current = false;
      sessionIdRef.current = next.id;
    }
    // Every committed snapshot advances the local revision so a runtime
    // response captured under an older revision cannot rewind newer state.
    sessionRevisionRef.current += 1;
    sessionRef.current = next;
    setSession(next);
    setScenario(next.scenario);
    adoptLearnerClock(next);
  }, [adoptLearnerClock, openingPreparationKey]);

  const applySession = useCallback((next: RoleplaySessionRecord) => {
    if (!enabledRef.current || openingPreparationKeyRef.current !== openingPreparationKey) return;
    const safetyOverride = next.status === 'crisis_paused';
    if (safetyOverride) {
      cancelTurn();
      setProcessing(false);
      terminalTransitionDeferredRef.current = false;
      terminalTransitionModeRef.current = null;
      deferredTerminalSessionRef.current = null;
      setTerminalTransitionPending(false);
    }
    const isTerminal = isTerminalStatus(next.status);
    if (terminalTransitionDeferredRef.current && isTerminal && !safetyOverride) {
      deferredTerminalSessionRef.current = next;
      if (terminalTransitionModeRef.current === 'text') commitSession(next);
      return;
    }
    const previousSession = sessionRef.current;
    const reachedTerminalFromPractice = isTerminal
      && previousSession?.id === next.id
      && !isTerminalStatus(previousSession.status);
    if (next.mode === 'voice' && reachedTerminalFromPractice && !safetyOverride) {
      // A time-limit or manual ending can arrive after the actor transcript has
      // been persisted but before its Realtime audio buffer finishes playing.
      // Keep PracticeScreen mounted so it can release this transition once the
      // browser reports that the current audio has drained.
      terminalTransitionDeferredRef.current = true;
      terminalTransitionModeRef.current = 'voice';
      deferredTerminalSessionRef.current = next;
      setTerminalTransitionPending(true);
      commitSession(next);
      return;
    }
    if (
      next.mode === 'text'
      && reachedTerminalFromPractice
      && previousSession
      && !safetyOverride
      && !immediateTextTerminalRef.current
      && (
        hasNewPersonaTurn(previousSession, next)
        || previousSession.status === 'closing'
        || endsWithPersonaReply(next)
      )
    ) {
      // Every final Text reply (a win's closing, or the reply persisted on a
      // turn or time limit) stays readable until the learner explicitly
      // continues, including a closing that was already visible before a later
      // completed poll.
      terminalTransitionDeferredRef.current = true;
      terminalTransitionModeRef.current = 'text';
      deferredTerminalSessionRef.current = next;
      setTerminalTransitionPending(true);
      commitSession(next);
      return;
    }
    commitSession(next);
  }, [cancelTurn, commitSession, openingPreparationKey]);

  const releaseTerminalTransition = useCallback(() => {
    if (!isCurrent()) return;
    terminalTransitionDeferredRef.current = false;
    terminalTransitionModeRef.current = null;
    setTerminalTransitionPending(false);
    const deferred = deferredTerminalSessionRef.current;
    deferredTerminalSessionRef.current = null;
    if (deferred) commitSession(deferred);
  }, [commitSession, isCurrent]);

  // Only send question_id when a specific roleplay item is targeted so a
  // single-roleplay flow keeps its existing request shape.
  const fetchSession = useCallback(
    (options?: { signal?: AbortSignal; timeout?: number }) => (options
      ? roleplay.getSession(flowName, flowRunId, questionId, options)
      : questionId === null
      ? roleplay.getSession(flowName, flowRunId)
      : roleplay.getSession(flowName, flowRunId, questionId)),
    [flowName, flowRunId, questionId],
  );

  const ensureOpeningPreparation = useCallback(() => {
    if (!isCurrent()) return Promise.resolve(null);
    const currentKey = `${flowName}:${flowRunId}:${questionId ?? ''}`;
    if (openingPreparationRef.current?.key === currentKey) {
      return Promise.resolve(openingPreparationRef.current.value);
    }
    if (openingPreparationPromiseRef.current?.key === currentKey) {
      return openingPreparationPromiseRef.current.value;
    }
    setOpeningPreparing(true);
    const request = (questionId === null
      ? roleplay.prepareOpening(flowName, flowRunId)
      : roleplay.prepareOpening(flowName, flowRunId, questionId))
      .then(({ data }) => {
        if (!isCurrent() || openingPreparationKeyRef.current !== currentKey) return null;
        openingPreparationRef.current = { key: currentKey, value: data };
        setOpeningPreparation(data);
        return data;
      })
      .catch(() => null)
      .finally(() => {
        if (openingPreparationPromiseRef.current?.key === currentKey) {
          openingPreparationPromiseRef.current = null;
        }
        if (openingPreparationKeyRef.current === currentKey) setOpeningPreparing(false);
      });
    openingPreparationPromiseRef.current = { key: currentKey, value: request };
    return request;
  }, [flowName, flowRunId, isCurrent, questionId]);

  const load = useCallback(async () => {
    if (!enabled) return;
    const requestedKey = openingPreparationKey;
    setLoading(true);
    setError(null);
    const epoch = operationEpochRef.current;
    try {
      const response = await fetchSession();
      // A late initial GET (development effect replay, or one that settles after
      // the learner already chose Practice again) must not overwrite the
      // prebrief or a newer attempt.
      if (!enabledRef.current || openingPreparationKeyRef.current !== requestedKey || operationEpochRef.current !== epoch) return;
      setScenario(response.data.scenario);
      if (response.data.session) applySession(response.data.session);
      else {
        sessionIdRef.current = null;
        setSession(null);
        setSecondsLeft(response.data.scenario.max_active_seconds);
        void ensureOpeningPreparation();
      }
      setLoading(false);
    } catch (requestError) {
      if (!enabledRef.current || openingPreparationKeyRef.current !== requestedKey || operationEpochRef.current !== epoch) return;
      setError(getErrorMessage(requestError, 'Unable to load this roleplay practice.'));
    } finally {
      if (enabledRef.current && openingPreparationKeyRef.current === requestedKey && operationEpochRef.current === epoch) setLoading(false);
    }
  }, [applySession, enabled, ensureOpeningPreparation, fetchSession, openingPreparationKey]);

  const refreshSession = useCallback(async () => {
    if (!isCurrent()) return;
    if (refreshRequestRef.current) return;
    const request = new AbortController();
    refreshRequestRef.current = request;
    const epoch = requestEpochRef.current;
    const beganDuringTurn = Boolean(turnRequestRef.current);
    let rejectAborted!: () => void;
    const aborted = new Promise<never>((_, reject) => {
      rejectAborted = () => reject(new DOMException('Saved attempt polling canceled.', 'AbortError'));
      request.signal.addEventListener('abort', rejectAborted, { once: true });
    });
    const timeout = setTimeout(() => request.abort(), 10000);
    try {
      const response = await Promise.race([
        fetchSession({ signal: request.signal, timeout: 10000 }), aborted,
      ]);
      if (!isCurrent() || request.signal.aborted || epoch !== requestEpochRef.current) return;
      // Polling must never authorize a provisional generation or overwrite an
      // early Text reply. Safety can still interrupt an in-flight stream.
      if (response.data.session && ((!beganDuringTurn && !turnRequestRef.current) || response.data.session.status === 'crisis_paused')) {
        applySession(response.data.session);
      }
    } catch {
      // A later poll can recover; the turn is never reposted from here.
    } finally {
      clearTimeout(timeout);
      request.signal.removeEventListener('abort', rejectAborted);
      if (refreshRequestRef.current === request) refreshRequestRef.current = null;
    }
  }, [applySession, fetchSession, isCurrent]);

  useEffect(() => {
    cancelTurn();
    sessionIdRef.current = null;
    sessionRef.current = null;
    learnerClockRef.current = null;
    runtimeRef.current = null;
    endingRef.current = false;
    timerEndRequested.current = false;
    terminalTransitionDeferredRef.current = false;
    terminalTransitionModeRef.current = null;
    deferredTerminalSessionRef.current = null;
    setTerminalTransitionPending(false);
    setLearnerClock(null);
    setProcessing(false);
    setRuntimeWaiting(false);
    setSession(null);
    setScenario(null);
    if (enabled) void load();
    else setLoading(false);
    return cancelTurn;
  }, [cancelTurn, enabled, load]);

  const startPractice = useCallback(async (mode: RoleplayMode) => {
    if (!isCurrent()) return null;
    cancelTurn();
    const epoch = operationEpochRef.current;
    retryTurnRef.current = null;
    setProcessing(true);
    setError(null);
    try {
      const preparedOpening = openingPreparationRef.current?.key === openingPreparationKey
        ? openingPreparationRef.current.value
        : await ensureOpeningPreparation();
      if (!isCurrent() || operationEpochRef.current !== epoch) return null;
      const response = questionId === null
        ? await roleplay.startSession(flowName, flowRunId, mode, preparedOpening?.token)
        : await roleplay.startSession(flowName, flowRunId, mode, preparedOpening?.token, questionId);
      if (!isCurrent() || operationEpochRef.current !== epoch) return null;
      applySession(response.data);
      openingPreparationRef.current = null;
      setOpeningPreparation(null);
      return response.data;
    } catch (requestError) {
      if (!isCurrent() || operationEpochRef.current !== epoch) return null;
      setError(getErrorMessage(requestError, 'Unable to start the roleplay.'));
      return null;
    } finally {
      if (isCurrent() && operationEpochRef.current === epoch) setProcessing(false);
    }
  }, [applySession, cancelTurn, ensureOpeningPreparation, flowName, flowRunId, isCurrent, openingPreparationKey, questionId]);

  const submitTurn = useCallback(async (
    text: string,
    inputMode: RoleplayMode = 'text',
    clientEventId = makeEventId(`learner-${inputMode}`),
    actorOutputMode?: RoleplayMode,
    onProgress?: (event: RoleplayTurnProgress) => void,
  ) => {
    if (!enabledRef.current || openingPreparationKeyRef.current !== openingPreparationKey || !session || !text.trim() || turnRequestRef.current || session.turn_processing) return null;
    // A stalled older-epoch poll must not occupy the single refresh flight
    // while this turn is active; safety polling restarts under the new epoch.
    cancelRefresh();
    const request = new AbortController();
    turnRequestRef.current = request;
    const epoch = ++requestEpochRef.current;
    const isCurrent = () => enabledRef.current && openingPreparationKeyRef.current === openingPreparationKey
      && !request.signal.aborted && requestEpochRef.current === epoch;
    const eventId = retryTurnRef.current?.text === text.trim()
      ? retryTurnRef.current.eventId : clientEventId;
    const measurement = (actorOutputMode ?? inputMode) === 'text' ? {
      recorder: startRoleplayPerformance({ flowName, sessionId: session.id, mode: 'text', inputMode }),
      sessionId: session.id,
      startedAt: performance.now(),
      learnerTurnId: null as number | null,
      firstVisible: false,
      requestCompleted: false,
    } : null;
    textPerformanceRef.current = measurement;
    const linkPerformance = (next: RoleplaySessionRecord) => {
      if (!measurement || next.id !== measurement.sessionId) return;
      const learner = next.turns.filter((turn) => turn.speaker === 'learner')
        .sort((a, b) => b.sequence - a.sequence)[0];
      if (learner) {
        measurement.learnerTurnId = learner.id;
        measurement.recorder.update({ learner_turn_id: learner.id });
      }
    };
    retryTurnRef.current = { text: text.trim(), eventId };
    setProcessing(true);
    setTurnActive(true);
    setError(null);
    // The server stops a negotiated clock when it accepts the turn. Freeze the
    // local projection at send time so the countdown does not keep ticking and
    // then jump back when the stopped snapshot arrives (display only; the
    // server value replaces this on the next payload).
    const projected = learnerClockRef.current;
    if (projected?.sessionId === session.id && projected.running) {
      const frozen: LearnerClockProjection = {
        ...projected,
        remainingMs: Math.max(0, projected.remainingMs - (Date.now() - projected.observedAt)),
        observedAt: Date.now(),
        running: false,
      };
      learnerClockRef.current = frozen;
      setLearnerClock(frozen);
    }
    try {
      const response = await roleplay.submitTurn(flowName, session.id, {
        text: text.trim(),
        input_mode: inputMode,
        ...(actorOutputMode ? { actor_output_mode: actorOutputMode } : {}),
        client_event_id: eventId,
      }, {
        signal: request.signal,
        onProgress: (event) => {
          if (!isCurrent() || event.session.id !== session.id) return;
          linkPerformance(event.session);
          if (event.type === 'preview') applySession({ ...event.session, turn_processing: true });
          // Provisional state is private to the Voice gate. Persisted UI state
          // and polling cannot accidentally start or release speculative audio.
          if (event.type === 'session' && event.session.status === 'crisis_paused') {
            applySession(event.session);
          }
          onProgress?.(event);
        },
      });
      if (!isCurrent()) return null;
      if (response.data.id !== session.id) throw new Error('The roleplay response belongs to a different attempt.');
      linkPerformance(response.data);
      if (measurement) {
        measurement.requestCompleted = true;
        measurement.recorder.update({ metrics: { backend_decision_ms: performance.now() - measurement.startedAt } });
        if (!['active', 'completed'].includes(response.data.status) || response.data.actor_directive) {
          measurement.recorder.finish(
            response.data.status === 'evaluation_paused' ? 'error' : 'cancelled',
            response.data.status === 'evaluation_paused' ? 'turn_failed' : 'interrupted',
          );
          if (textPerformanceRef.current === measurement) textPerformanceRef.current = null;
        }
      }
      applySession(response.data);
      retryTurnRef.current = null;
      return response.data;
    } catch (requestError) {
      if (!isCurrent()) return null;
      // A disconnected stream may already have accepted/persisted the turn.
      // Reconcile by GET, never by reposting with a new idempotency key.
      const recoveryController = new AbortController();
      const abortRecovery = () => recoveryController.abort();
      request.signal.addEventListener('abort', abortRecovery, { once: true });
      let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
      try {
        const deadline = new Promise<never>((_, reject) => {
          recoveryTimer = setTimeout(() => {
            recoveryController.abort();
            reject(new Error('Saved attempt recovery timed out.'));
          }, 10000);
        });
        const recovery = await Promise.race([
          fetchSession({ signal: recoveryController.signal, timeout: 10000 }), deadline,
        ]);
        if (!isCurrent()) return null;
        if (recovery.data.session?.id === session.id) {
          linkPerformance(recovery.data.session);
          applySession(recovery.data.session);
        }
      } catch { /* The retained event id makes an explicit same-text retry safe. */ }
      finally {
        clearTimeout(recoveryTimer);
        request.signal.removeEventListener('abort', abortRecovery);
      }
      if (!isCurrent()) return null;
      measurement?.recorder.finish('error', 'turn_failed');
      if (textPerformanceRef.current === measurement) textPerformanceRef.current = null;
      const message = getErrorMessage(
        requestError,
        'The connection was interrupted. Your message may already be saved. Check the transcript before retrying.',
      );
      // A stale Realtime transcription can finish after the backend has already
      // closed the learner turn. The voice gate normally prevents this request;
      // if one was already in flight, do not expose the internal session guard.
      if (
        inputMode !== 'voice'
        || !message.toLowerCase().includes('not accepting learner turns')
      ) {
        setError(message);
      }
      return null;
    } finally {
      if (isCurrent()) {
        turnRequestRef.current = null;
        setProcessing(false);
        setTurnActive(false);
      }
    }
  }, [applySession, cancelRefresh, fetchSession, flowName, openingPreparationKey, session]);

  useEffect(() => {
    const measurement = textPerformanceRef.current;
    if (!measurement || measurement.sessionId !== session?.id || !measurement.learnerTurnId) return;
    // Observe the committed React view, not merely receipt of an SSE frame.
    const replyVisible = session.turns.some((turn) => turn.speaker === 'persona'
      && turn.reply_to_id === measurement.learnerTurnId && !turn.metadata?.safety_only);
    if (replyVisible && !measurement.firstVisible) {
      measurement.firstVisible = true;
      measurement.recorder.update({ metrics: { text_first_visible_ms: performance.now() - measurement.startedAt } });
    }
    if (measurement.firstVisible && measurement.requestCompleted && !processing && !session.turn_processing) {
      measurement.recorder.update({ metrics: { controls_ready_ms: performance.now() - measurement.startedAt } });
      measurement.recorder.finish('ok');
      textPerformanceRef.current = null;
    }
  }, [processing, session]);

  const persistActorTurn = useCallback(async (payload: {
    text: string;
    generation: number;
    client_event_id: string;
    provider_response_id?: string;
    persona_expression?: RoleplayExpression;
    usage?: Record<string, unknown>;
  }) => {
    if (!isCurrent() || !session) return null;
    // Beginning a save never invalidates other work; only End, a new attempt,
    // the prebrief, safety, unmount or explicit recovery fence this result.
    const sessionId = session.id;
    const epoch = operationEpochRef.current;
    if (session.mode === 'voice' && session.status === 'closing') {
      // The backend completes the attempt as soon as the closing transcript is
      // persisted. Keep the practice screen mounted until Realtime confirms that
      // the corresponding WebRTC output buffer has finished playing.
      terminalTransitionDeferredRef.current = true;
      terminalTransitionModeRef.current = 'voice';
      setTerminalTransitionPending(true);
    }
    try {
      const response = await roleplay.persistActorTurn(flowName, sessionId, payload);
      if (!isOperationCurrent(sessionId, epoch)) return null;
      applySession(response.data);
      return response.data;
    } catch (requestError) {
      if (!isOperationCurrent(sessionId, epoch)) return null;
      setError(getErrorMessage(requestError, 'The voice response could not be saved. Reconnect to retry.'));
      return null;
    }
  }, [applySession, flowName, isCurrent, isOperationCurrent, session]);

  const retryEvaluation = useCallback(async () => {
    if (!isCurrent() || !session || processing) return null;
    cancelRefresh();
    inputRecoveryEpochRef.current += 1;
    const sessionId = session.id;
    const epoch = ++operationEpochRef.current;
    setProcessing(true);
    setError(null);
    try {
      const response = await roleplay.retryEvaluation(flowName, sessionId);
      if (!isOperationCurrent(sessionId, epoch)) return null;
      applySession(response.data);
      return response.data;
    } catch (requestError) {
      if (!isOperationCurrent(sessionId, epoch)) return null;
      setError(getErrorMessage(requestError, 'The evaluator is still unavailable. Please try again.'));
      return null;
    } finally {
      if (isOperationCurrent(sessionId, epoch)) setProcessing(false);
    }
  }, [applySession, cancelRefresh, flowName, isCurrent, isOperationCurrent, processing, session]);

  const retryActor = useCallback(async () => {
    if (!isCurrent() || !session || processing) return null;
    cancelRefresh();
    inputRecoveryEpochRef.current += 1;
    const sessionId = session.id;
    const epoch = ++operationEpochRef.current;
    setProcessing(true);
    setError(null);
    try {
      const response = await roleplay.retryActor(flowName, sessionId, 'text');
      if (!isOperationCurrent(sessionId, epoch)) return null;
      applySession(response.data);
      return response.data;
    } catch (requestError) {
      if (!isOperationCurrent(sessionId, epoch)) return null;
      setError(getErrorMessage(requestError, 'The actor is still unavailable. Please try again.'));
      return null;
    } finally {
      if (isOperationCurrent(sessionId, epoch)) setProcessing(false);
    }
  }, [applySession, cancelRefresh, flowName, isCurrent, isOperationCurrent, processing, session]);

  const endPractice = useCallback(async (reason = 'learner_ended') => {
    if (!isCurrent() || !session || endingRef.current) return null;
    cancelTurn();
    const sessionId = session.id;
    const epoch = operationEpochRef.current;
    endingRef.current = true;
    setProcessing(true);
    try {
      const response = await roleplay.endSession(flowName, sessionId, reason);
      if (!isOperationCurrent(sessionId, epoch)) return null;
      // A manual End stays immediate; automatic limits keep a final reply readable.
      immediateTextTerminalRef.current = reason !== 'time_limit';
      try {
        applySession(response.data);
      } finally {
        immediateTextTerminalRef.current = false;
      }
      // A clocked attempt may refuse an early time-limit request; the returned
      // state re-syncs the projection and a later expiry can ask again.
      if (reason === 'time_limit' && !isTerminalStatus(response.data.status)) {
        timerEndRequested.current = false;
      }
      return response.data;
    } catch (requestError) {
      if (!isOperationCurrent(sessionId, epoch)) return null;
      setError(getErrorMessage(requestError, 'Unable to end the practice.'));
      return null;
    } finally {
      endingRef.current = false;
      if (isOperationCurrent(sessionId, epoch)) {
        setProcessing(false);
        // Anything that began while End was in flight carries pre-End state.
        if (isTerminalStatus(sessionRef.current?.status ?? 'active')) operationEpochRef.current += 1;
      }
    }
  }, [applySession, cancelTurn, flowName, isCurrent, isOperationCurrent, session]);

  const resolveSafety = useCallback(async (scope: 'persona' | 'self') => {
    if (!isCurrent() || !session || processing) return null;
    cancelRefresh();
    inputRecoveryEpochRef.current += 1;
    const sessionId = session.id;
    const epoch = ++operationEpochRef.current;
    setProcessing(true);
    try {
      const response = await roleplay.resolveSafety(flowName, sessionId, scope);
      if (!isOperationCurrent(sessionId, epoch)) return null;
      applySession(response.data);
      return response.data;
    } catch (requestError) {
      if (!isOperationCurrent(sessionId, epoch)) return;
      setError(getErrorMessage(requestError, 'Unable to continue this practice.'));
      return null;
    } finally {
      if (isOperationCurrent(sessionId, epoch)) setProcessing(false);
    }
  }, [applySession, cancelRefresh, flowName, isCurrent, isOperationCurrent, processing, session]);

  const createDebrief = useCallback(async (reflection: string) => {
    if (!isCurrent() || !session || processing) return null;
    const sessionId = session.id;
    const epoch = operationEpochRef.current;
    setProcessing(true);
    setError(null);
    try {
      const response = await roleplay.debrief(flowName, sessionId, reflection);
      if (!isOperationCurrent(sessionId, epoch)) return null;
      applySession(response.data);
      return response.data;
    } catch (requestError) {
      if (!isOperationCurrent(sessionId, epoch)) return null;
      setError(getErrorMessage(requestError, 'Unable to prepare your debrief.'));
      return null;
    } finally {
      if (isOperationCurrent(sessionId, epoch)) setProcessing(false);
    }
  }, [applySession, flowName, isCurrent, isOperationCurrent, processing, session]);

  const prepareRetry = useCallback(async () => {
    if (!isCurrent() || !scenario || processing) return;
    cancelTurn();
    const epoch = operationEpochRef.current;
    retryTurnRef.current = null;
    setProcessing(true);
    setError(null);
    terminalTransitionDeferredRef.current = false;
    terminalTransitionModeRef.current = null;
    deferredTerminalSessionRef.current = null;
    setTerminalTransitionPending(false);
    sessionIdRef.current = null;
    sessionRef.current = null;
    learnerClockRef.current = null;
    setLearnerClock(null);
    timerEndRequested.current = false;
    openingPreparationRef.current = null;
    openingPreparationPromiseRef.current = null;
    setOpeningPreparation(null);
    setSession(null);
    setSecondsLeft(scenario.max_active_seconds);
    try {
      await ensureOpeningPreparation();
    } finally {
      if (isCurrent() && operationEpochRef.current === epoch) setProcessing(false);
    }
  }, [cancelTurn, ensureOpeningPreparation, isCurrent, processing, scenario]);

  /**
   * Apply a fresh server snapshot returned by a runtime action or the Realtime
   * bootstrap. During an in-flight learner stream only the clock is adopted so
   * a snapshot cannot overwrite an early reply; crisis still interrupts.
   */
  const applyRuntimeSession = useCallback((
    snapshot: RoleplaySessionRecord,
    options: { revisionAtSend?: number } = {},
  ) => {
    if (!isCurrent() || snapshot.id !== sessionIdRef.current) return;
    const current = sessionRef.current;
    // A snapshot can never revive an attempt this browser already saw end.
    if (current && isTerminalStatus(current.status) && !isTerminalStatus(snapshot.status)) return;
    // Safety and server-decided endings are authoritative regardless of age.
    if (snapshot.status === 'crisis_paused' || (isTerminalStatus(snapshot.status) && current && !isTerminalStatus(current.status))) {
      applySession(snapshot);
      return;
    }
    // REV-001: a runtime response that the server produced before a newer
    // learner turn/actor save (or that arrives while a stream is in flight)
    // may only adopt clock state; its transcript/directive/mode are older than
    // what this browser already committed. adoptLearnerClock itself ignores a
    // clock snapshot older than the one already projected.
    const stale = Boolean(turnRequestRef.current)
      || (options.revisionAtSend !== undefined && options.revisionAtSend !== sessionRevisionRef.current)
      || (current !== null && snapshot.turns.length < current.turns.length);
    if (stale) {
      adoptLearnerClock(snapshot);
      return;
    }
    applySession(snapshot);
  }, [adoptLearnerClock, applySession, isCurrent]);

  const sendRuntimeAction = useCallback((
    action: RoleplayRuntimeAction['action'],
    extras: Pick<RoleplayRuntimeAction, 'input_event_id' | 'actor_generation' | 'ready_reason'> = {},
  ) => {
    // REV-002: the attempt and operation identity are fixed when the action is
    // enqueued. Work that waited behind a slow action for an attempt that has
    // since ended, been replaced or unmounted is discarded before any request;
    // same-attempt clock ownership (runtime id, sequence, epoch) is still
    // derived at execution so serialized actions stay valid.
    const enqueuedSessionId = sessionIdRef.current;
    const enqueuedEpoch = operationEpochRef.current;
    const enqueuedRecoveryEpoch = inputRecoveryEpochRef.current;
    const run = async (): Promise<RuntimeActionOutcome | null> => {
      const current = sessionRef.current;
      if (!current || enqueuedSessionId === null || current.id !== enqueuedSessionId
        || sessionIdRef.current !== enqueuedSessionId) return null;
      // A suspend only stops the clock of the attempt it was enqueued for, so
      // it survives the End/unmount epoch bump that follows it; every other
      // action is obsolete once the operation epoch moved.
      if (action !== 'suspend' && operationEpochRef.current !== enqueuedEpoch) return null;
      // R2-001: a suspend queued by a disconnect is obsolete once a later
      // explicit recovery (Text actor retry, evaluator retry, safety resolution)
      // restored usable input on this attempt; sending it would stop that clock.
      if (action === 'suspend' && inputRecoveryEpochRef.current !== enqueuedRecoveryEpoch) return null;
      if (isTerminalStatus(current.status) || current.status === 'crisis_paused') return null;
      const currentEpoch = learnerClockRef.current?.sessionId === current.id
        ? learnerClockRef.current.epoch
        : current.learner_clock?.epoch ?? null;
      // Only a claim negotiates (or adopts) a clock; every other action needs one.
      if (action !== 'claim' && currentEpoch === null) return null;
      if (runtimeRef.current?.sessionId !== current.id) {
        runtimeRef.current = { sessionId: current.id, runtimeId: makeEventId('runtime'), sequence: 0 };
      }
      const runtime = runtimeRef.current;
      runtime.sequence += 1;
      const payload: RoleplayRuntimeAction = {
        action,
        runtime_id: runtime.runtimeId,
        event_id: makeEventId(action),
        sequence: runtime.sequence,
        epoch: currentEpoch ?? '',
        learner_clock_version: 1,
        ...extras,
      };
      const sessionId = current.id;
      const epoch = operationEpochRef.current;
      const revisionAtSend = sessionRevisionRef.current;
      let response: { data: RoleplaySessionRecord } | null = null;
      for (let attempt = 0; attempt < 2 && !response; attempt += 1) {
        try {
          response = await roleplay.runtimeAction(flowName, sessionId, payload);
        } catch (requestError) {
          // One retry with the same event id covers a lost transport; a server
          // answer (even a rejection) is final.
          if (attempt === 1 || hasHttpStatus(requestError)) return null;
          // R3-001: the retry must still be valid. A request that became stale
          // while its first attempt failed (End/new attempt, or an explicit
          // recovery that restored usable input) is not resent.
          if (sessionIdRef.current !== enqueuedSessionId
            || (action !== 'suspend' && operationEpochRef.current !== enqueuedEpoch)
            || (action === 'suspend' && inputRecoveryEpochRef.current !== enqueuedRecoveryEpoch)) return null;
        }
      }
      if (!response || !isOperationCurrent(sessionId, epoch)) return null;
      // Accepted results carry the started/stopped clock; a refusal such as
      // stale_epoch still returns the fresh state, which simply re-syncs.
      applyRuntimeSession(response.data, { revisionAtSend });
      // R2-002: callers (the voice hook's input recovery) must learn whether the
      // server actually acknowledged the action; a discarded, failed or refused
      // action is not an acknowledgment.
      return response.data.runtime_action ?? null;
    };
    const queued = runtimeQueueRef.current.then(run, run);
    runtimeQueueRef.current = queued.then(() => undefined, () => undefined);
    return queued.catch(() => null);
  }, [applyRuntimeSession, flowName, isOperationCurrent]);

  const claimRuntime = useCallback(() => sendRuntimeAction('claim'), [sendRuntimeAction]);
  const suspendRuntime = useCallback(() => sendRuntimeAction('suspend'), [sendRuntimeAction]);
  const readyRuntime = useCallback((ready: RoleplayRuntimeReady) => sendRuntimeAction('ready', {
    ready_reason: ready.reason,
    ...(typeof ready.actorGeneration === 'number' ? { actor_generation: ready.actorGeneration } : {}),
    ...(ready.inputEventId ? { input_event_id: ready.inputEventId } : {}),
  }), [sendRuntimeAction]);
  const inputFinalizing = useCallback(
    (eventId: string) => sendRuntimeAction('input_finalizing', { input_event_id: eventId }),
    [sendRuntimeAction],
  );

  // Legacy attempts (no negotiated clock) keep the local countdown that pauses
  // for local processing and system waits.
  useEffect(() => {
    if (learnerClock || session?.status !== 'active' || processing || session.turn_processing || runtimeWaiting) return undefined;
    const intervalId = window.setInterval(() => {
      setSecondsLeft((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [learnerClock, processing, runtimeWaiting, session?.id, session?.status, session?.turn_processing]);

  // A negotiated clock is projected from the server snapshot only while the
  // server reported it running; nothing local restarts or pauses it.
  useEffect(() => {
    if (!learnerClock?.running || session?.status !== 'active') return undefined;
    const intervalId = window.setInterval(() => {
      setSecondsLeft(projectSecondsLeft(learnerClock));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [learnerClock, session?.status]);

  useEffect(() => {
    if (
      session?.status !== 'active'
      || secondsLeft > 0
      || processing
      || session.turn_processing
      || (session.mode === 'voice' && Boolean(session.actor_directive))
      || timerEndRequested.current
    ) return;
    // A clocked attempt asks to end only when the projected server clock
    // reaches zero; a paused server clock never triggers the request.
    if (learnerClock ? !learnerClock.running : runtimeWaiting) return;
    timerEndRequested.current = true;
    void endPractice('time_limit');
  }, [
    endPractice,
    learnerClock,
    processing,
    runtimeWaiting,
    secondsLeft,
    session?.actor_directive,
    session?.mode,
    session?.status,
    session?.turn_processing,
  ]);

  const aiSafetyStatus = String(session?.ai_safety?.status ?? '');
  const shouldPoll = Boolean(session) && (
    aiSafetyStatus === 'pending'
    || session?.status === 'closing'
    || Boolean(session?.turn_processing)
    // Safety can resolve on the server while the local learner request is
    // still waiting for its first stream frame.
    || turnActive
    // The debrief is being generated by another request (second tab, reload).
    || Boolean(session?.debrief_pending)
    // An idle successor whose predecessor still has a check pending.
    || (session !== null && !isTerminalStatus(session.status) && (session.safety_checks_pending ?? 0) > 0)
    // A pending Text directive owned by another request lands without a
    // duplicate generation; only a reported actor_error needs an explicit retry.
    || (session?.mode === 'text' && session.status === 'active'
      && Boolean(session.actor_directive) && !session.actor_error)
  );
  useEffect(() => {
    if (!shouldPoll) return undefined;
    const intervalId = window.setInterval(() => void refreshSession(), 750);
    return () => window.clearInterval(intervalId);
  }, [refreshSession, session?.id, shouldPoll]);

  const latestPersonaTurns = useMemo(() => {
    const turns = session?.turns ?? [];
    // Safety-only clarification is outside the conversation, so it never hides
    // the persona line the learner was replying to.
    const latestLearnerSequence = turns.reduce(
      (latest, turn) => turn.speaker === 'learner' && !turn.metadata?.safety_only
        ? Math.max(latest, turn.sequence)
        : latest,
      Number.NEGATIVE_INFINITY,
    );
    return turns
      .filter(
        (turn) => turn.speaker === 'persona'
          && turn.sequence > latestLearnerSequence
          && !turn.metadata?.safety_only,
      )
      .sort((left, right) => left.sequence - right.sequence || left.id - right.id);
  }, [session?.turns]);
  const latestPersonaTurn = latestPersonaTurns[latestPersonaTurns.length - 1] ?? null;

  return {
    flowName,
    flowRunId,
    questionId,
    scenario,
    session,
    screen: terminalTransitionPending ? 'practice' : screenForSession(session),
    loading,
    processing: processing || Boolean(session?.turn_processing) || Boolean(session?.debrief_pending),
    error,
    secondsLeft,
    openingPreparation,
    openingPreparing,
    terminalTransitionPending,
    runtimeWaiting,
    setRuntimeWaiting,
    latestPersonaTurns,
    latestPersonaTurn,
    applySession,
    releaseTerminalTransition,
    startPractice,
    submitTurn,
    persistActorTurn,
    retryEvaluation,
    retryActor,
    endPractice,
    resolveSafety,
    createDebrief,
    prepareRetry,
    reload: load,
    claimRuntime,
    suspendRuntime,
    readyRuntime,
    inputFinalizing,
    applyRuntimeSession,
  };
};

export type RoleplaySessionController = ReturnType<typeof useRoleplaySession>;
